import { defineStore } from "pinia";
import _ from "lodash";
import { BankCustodian } from "@/admin/types";
import {
    FetchState,
    InvestmentCustodian,
    OnboardingWizard,
    OnboardingWizardStatus,
    UserOnboardingStepKey,
    UserOnboardingSteps,
    WizardState
} from "@/shared/types";
import {
    getUserWizardProgress,
    setUserWizardComplete,
    UserWizardState
} from "@/shared/api/userService";
import * as DrivewealthInvestmentService from "@/shared/api/drivewealthService";
import * as UserOnboarding from "@/shared/userOnboarding";
import { retryOnNotFoundError } from "@/shared/retryHelper";
import { useUserStore } from "@/shared/store/user";
import { useDrivewealthStore } from "@/shared/store/drivewealth";
import { getStoragePaths } from "@/shared/storeHelper";
import { useInvestmentThresholdStore } from "@/shared/store/investmentThreshold";
import { useCustodianSettingsStore } from "@/shared/store/custodianSettings";

export const initialState: WizardState = {
    onboardingWizardState: FetchState.Uninitialized,
    onboardingWizard: null,
    transitionToEmployee: false,
    stepsCompleting: null,
    allowDrivewealthPreEnrollment: false,
    showUnsavedChangesPrompt: false
};

const finishSteps = [ UserOnboardingSteps.Finish, UserOnboardingSteps.ClaimAccountFinished ];

const userStepTypeToLabel = (stepId): string => {
    switch (stepId) {
        case UserOnboardingSteps.ClaimAccountTerms:
            return "Terms & Conditions";
        case UserOnboardingSteps.ClaimAccountPersona:
            return "Your HSA";
        case UserOnboardingSteps.ClaimAccountFinished:
            return "Finish";

        case UserOnboardingSteps.Terms:
            return "Terms and conditions";
        case UserOnboardingSteps.You:
            return "Personal details";
        case UserOnboardingSteps.YourPlan:
            return "Health plan";
        case UserOnboardingSteps.YourHsa:
            return "Your HSA";
        case UserOnboardingSteps.YourFamily:
            return "Dependent details";
        case UserOnboardingSteps.VerifyIdentity:
            return "Review and submit";
        case UserOnboardingSteps.Investments:
            return "Investments";
        case UserOnboardingSteps.Finish:
            return "Confirmation";
    }

    return "";
};

export const useWizardStore = defineStore("wizard", {
    persist: {
        storage: localStorage,
        paths: getStoragePaths("wizard"),
    },
    state: () => (_.cloneDeep(initialState)),
    getters: {
        wizardState () {
            return this.onboardingWizardState;
        },
        isLoaded () {
            return this.onboardingWizardState === FetchState.Loaded;
        },
        onboardingStepIndex () {
            return (stepKey: UserOnboardingStepKey | any) => {
                const statuses: OnboardingWizardStatus[] = this.shownStatuses;
                return statuses.findIndex(s => s.stepKey === stepKey);
            };
        },
        maxAllowedStep () {
            if (this.shownStatuses.length === 0) {
                return null;
            }

            const lastIndex = this.shownStatuses.length - 1;
            for (let i = 0; i < lastIndex; i++) {
                if (!this.shownStatuses[i].complete) {
                    return i;
                }
            }
            return lastIndex;
        },
        maxAllowedStepKey () {
            const statuses: OnboardingWizardStatus[] = this.shownStatuses;
            if (statuses.length === 0) {
                return null;
            }

            let firstIncomplete = statuses.find(s => !s.complete);
            if (!firstIncomplete) {
                firstIncomplete = statuses.reverse()[0];
            }

            return firstIncomplete.stepKey;
        },
        isNavigationAllowed () {
            return stepKey => {
                if (stepKey === this.maxAllowedStepKey) {
                    return true;
                }

                for (const status of this.shownStatuses) {
                    if (status.stepKey === this.maxAllowedStepKey) {
                        return false;
                    } // found the last allowed step first, so no dice
                    if (status.stepKey === stepKey) {
                        return true;
                    }
                }

                return false; // not in the list at all!
            };
        },
        isStepComplete () {
            return (stepKey: UserOnboardingStepKey) => {
                const statuses: OnboardingWizardStatus[] = this.shownStatuses;
                if (statuses.length === 0) {
                    return false;
                }

                const status = statuses.find(s => s.stepKey === stepKey);
                return status && status.complete;
            };
        },
        isComplete () {
            return this.shownStatuses.some(f => f.complete && finishSteps.some(fs => fs === f.step));
        },
        shownStatuses () {
            if (!this.onboardingWizard || !this.onboardingWizard.statuses || !this.isLoaded) {
                return [];
            }

            return this.onboardingWizard.statuses
                .filter(status => this.isDrivewealthPreenrollmentAllowed || status.step !==
                    UserOnboardingSteps.Investments);
        },
        isDrivewealthPreenrollmentAllowed () {
            const investmentThresholdStore = useInvestmentThresholdStore();
            const custodian = investmentThresholdStore.investmentProvider;

            if (!custodian) {
                return false;
            }

            return custodian === InvestmentCustodian.Drivewealth;
        },
        useClaimAccountFlow () {
           return this.shownStatuses.some(x => x.step === UserOnboardingSteps.ClaimAccountTerms);
        }
    },
    actions: {
        setOnboardingWizard (data: { wizardData: UserWizardState }) {
            if (!data.wizardData || !data.wizardData.wizardProgressSteps) {
                return;
            }

            const statuses = data.wizardData.wizardProgressSteps.map(w => {
                const onboardingWizardStatus: OnboardingWizardStatus = {
                    complete: w.hasUserCompleted,
                    step: w.wizardStepNumber,
                    stepKey: UserOnboarding.keyFromStep(w.wizardStepNumber),
                    order: w.order,
                    label: userStepTypeToLabel(w.wizardStepNumber),
                };

                return onboardingWizardStatus;
            }).sort((a, b) => a.order - b.order);

            const onboardingWizard: OnboardingWizard = {
                statuses
            };
            this.onboardingWizard = onboardingWizard;
        },
        setOnboardingFetchState (s: FetchState) {
            this.onboardingWizardState = s;
        },
        setStepComplete (wizardStep: number) {
            const step = this.onboardingWizard.statuses.find(s => s.step === wizardStep);
            if (step) {
                step.complete = true;
            }
        },
        setStepIncomplete (wizardStep: number) {
            const step = this.onboardingWizard.statuses.find(s => s.step === wizardStep);
            if (step) {
                step.complete = false;
            }
        },
        setStepCompleting (step: UserOnboardingSteps) {
            const stepsComp = this.stepsCompleting || [];
            if (stepsComp.includes(step)) {
                return;
            }
            stepsComp.push(step);
            this.stepsCompleting = stepsComp;
        },
        clearStepCompleting (step: UserOnboardingSteps) {
            if (!this.stepsCompleting || this.stepsCompleting.length === 0) {
                return;
            }
            const idx = this.stepsCompleting.indexOf(step);
            if (idx > -1) {
                this.stepsCompleting.splice(idx, 1);
            }
        },
        setTransitionToEmployee (value: boolean) {
            this.transitionToEmployee = value;
        },
        addFamilyPlanStep () {
            let stepAfterFamily = this.onboardingWizard.statuses
                .find(s => s.step === UserOnboardingSteps.Investments || s.step === UserOnboardingSteps.VerifyIdentity);

            const custodianSettingsStore = useCustodianSettingsStore();
            if (custodianSettingsStore.userHsaCustodian === BankCustodian.HsaBank) {
              stepAfterFamily = this.onboardingWizard.statuses.find(s => s.step === UserOnboardingSteps.Terms);
            }

            const familyStatus: OnboardingWizardStatus = {
                complete: false,
                step: UserOnboardingSteps.YourFamily,
                stepKey: UserOnboarding.keyFromStep(UserOnboardingSteps.YourFamily),
                label: userStepTypeToLabel(UserOnboardingSteps.YourFamily),
                order: stepAfterFamily.order - 0.5,
            };

            this.onboardingWizard.statuses.push(familyStatus);
            this.onboardingWizard.statuses.sort((a, b) => a.order - b.order);
        },
        removeFamilyPlanStep () {
            const stepIndex = this.onboardingWizard.statuses.findIndex(s => s.step === UserOnboardingSteps.YourFamily);
            this.onboardingWizard.statuses.splice(stepIndex, 1);
            this.onboardingWizard.statuses.sort((a, b) => a.order - b.order);
        },
        setPersonalDetailsStepIncomplete () {
            this.setStepIncomplete(UserOnboardingSteps.You);
        },
        setAllowPreenrollment (val: { allowDrivewealthPreEnrollment: boolean }) {
            this.allowDrivewealthPreEnrollment = val.allowDrivewealthPreEnrollment;
        },
        setWizardComplete (): Promise<any> {
            return new Promise(() => {
                for (const step of finishSteps) {
                    this.setStepComplete(step);
                }
            });
        },
        getWizardState (args?: { force?: boolean }): Promise<any> {
            const defaultValue = { allowDrivewealthPreEnrollment: false };

            if (!(args && args.force) && this.onboardingWizardState !== FetchState.Uninitialized) {
                return Promise.resolve();
            }

            this.setOnboardingFetchState(FetchState.Loading);

            const promiseOfDrivewealth = () =>
                DrivewealthInvestmentService.getEmployeeInvestmentTypes(null, null)
                    .then(response => {
                        return { allowDrivewealthPreEnrollment: response.data.allowPreEnrollment };
                    });

            const retryOnNotFoundOfDrivewealth = retryOnNotFoundError(promiseOfDrivewealth)
                .catch(_ => defaultValue);

            const userserviceCall = getUserWizardProgress()
                .then(response => {
                    if (!response) {
                        throw { message: "There was a problem reaching our servers", payload: response };
                    }
                    this.setOnboardingWizard({ wizardData: response.data });
                    return this.onboardingWizard;
                }).catch(err => {
                    throw { message: "User api returned non-OK status", payload: err };
                });
            const investmentThresholdStore = useInvestmentThresholdStore();
            const investmentThresholdPromise = investmentThresholdStore.loadInvestmentThresholds()
                .catch(_ => Promise.resolve());
                // swallow the error b/c the onboarding wizard can progress without it

            return Promise.all([
                retryOnNotFoundOfDrivewealth,
                userserviceCall,
                investmentThresholdPromise
            ]).then(_ => {
                return retryOnNotFoundOfDrivewealth.then(data => {
                    this.setAllowPreenrollment(data);
                    this.setOnboardingFetchState(FetchState.Loaded);
                });
            });
        },
        setStepKeyComplete (stepKey: UserOnboardingStepKey) {
            if (this.isComplete) {
                return Promise.resolve();
            }

            const step = UserOnboarding.stepFromKey(stepKey);
            const stepId: number = <number>step;
            const stepsCompleting = this.stepsCompleting || [];

            if (stepsCompleting.includes(step)) {
                return Promise.resolve();
            } else {
                this.setStepCompleting(step);
            }

            return setUserWizardComplete(stepId).then(_ => {
                this.setStepComplete(stepId);
            }).finally(() => this.clearStepCompleting(step));
        },
        transitionEmployee () {
            if (this.transitionToEmployee === false) {
                return Promise.resolve();
            }
            const userStore = useUserStore();
            const driveWealthWizardStore = useDrivewealthStore();
            return userStore.refreshUser().then(_ => {
                this.setTransitionToEmployee(false);
                return driveWealthWizardStore.loadAllowedInvestmentAccountTypes();
            });
        },
        setIsFamilyPlan (isFamilyPlan: boolean): Promise<any> {
            return new Promise((resolve, reject) => {
                if (isFamilyPlan &&
                    !this.onboardingWizard.statuses.some(s => s.step === UserOnboardingSteps.YourFamily)) {
                    this.addFamilyPlanStep();
                } else if (!isFamilyPlan &&
                    this.onboardingWizard.statuses.some(s => s.step === UserOnboardingSteps.YourFamily)) {
                    this.removeFamilyPlanStep();
                }
            });
        },
        confirmUnsavedChanges () {
            this.showUnsavedChangesPrompt = true;
        },
        unsavedChangesConfirmed () {
            this.showUnsavedChangesPrompt = false;
        }
    }
});
